
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PDFTemplateMapper from '@/components/student-certification/certificate-templates/PDFTemplateMapper';
import { CertificateTemplate, CertificateTemplateElement } from '@/types/certificateTemplate';

const EditTemplateMappingPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<CertificateTemplate | null>(null);
  const [mappedFields, setMappedFields] = useState<CertificateTemplateElement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!templateId) {
      toast.error('Template ID is missing');
      navigate('/certificate-templates');
      return;
    }
    
    // Load template from session storage (if available from previous steps)
    const storedTemplate = sessionStorage.getItem('editTemplate');
    if (storedTemplate) {
      try {
        const parsedTemplate = JSON.parse(storedTemplate);
        setTemplate(parsedTemplate);
        
        // Set mapped fields from template
        if (parsedTemplate.elements && parsedTemplate.elements.length > 0) {
          setMappedFields(parsedTemplate.elements);
        }
      } catch (error) {
        console.error('Error parsing stored template:', error);
      } finally {
        setLoading(false);
      }
    } else {
      toast.error('No template data found. Please start from the beginning.');
      navigate(`/certificate-templates/edit/${templateId}/basic-info`);
    }
  }, [templateId, navigate]);

  const handleSaveMapping = (fields: CertificateTemplateElement[]) => {
    setMappedFields(fields);
    
    // Update template in session storage
    if (template) {
      const updatedTemplate = {
        ...template,
        elements: fields
      };
      
      setTemplate(updatedTemplate);
      sessionStorage.setItem('editTemplate', JSON.stringify(updatedTemplate));
      toast.success('Field mapping saved successfully');
    }
  };

  const handlePrevious = () => {
    if (templateId) {
      navigate(`/certificate-templates/edit/${templateId}/upload`);
    }
  };

  const handleNext = () => {
    if (templateId) {
      navigate(`/certificate-templates/edit/${templateId}/preview`);
    }
  };

  const handleCancel = () => {
    navigate('/certificate-templates');
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // If no PDF template is available, show message and option to skip
  if (!template?.metadata?.isPdf || !template?.metadata?.pdfUrl) {
    return (
      <div className="container mx-auto py-8">
        <Helmet>
          <title>Edit Certificate Template - Field Mapping | NALC</title>
        </Helmet>
        
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Edit Certificate Template</h1>
          <p className="text-muted-foreground mt-2">
            Step 3: Map data fields to your PDF template
          </p>
        </div>
        
        <div className="flex justify-center mb-6">
          <div className="flex items-center">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
                ✓
              </div>
              <span className="text-sm mt-1">Basic Info</span>
            </div>
            <div className="w-12 h-1 bg-primary mx-1"></div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
                ✓
              </div>
              <span className="text-sm mt-1">PDF Upload</span>
            </div>
            <div className="w-12 h-1 bg-primary mx-1"></div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                3
              </div>
              <span className="text-sm mt-1 text-primary font-medium">Field Mapping</span>
            </div>
            <div className="w-12 h-1 bg-muted mx-1"></div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
                4
              </div>
              <span className="text-sm mt-1">Preview</span>
            </div>
          </div>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Field Mapping</CardTitle>
            <CardDescription>
              No PDF template was uploaded, so field mapping is not available. You can continue to the preview step or go back to upload a PDF.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="py-12 text-center">
              <p className="text-muted-foreground">No PDF template available for field mapping</p>
              <div className="mt-8 flex justify-center gap-4">
                <Button variant="outline" onClick={handlePrevious}>
                  <ChevronLeft className="mr-2 h-4 w-4" /> Go Back to Upload
                </Button>
                <Button onClick={handleNext}>
                  Skip to Preview <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <Helmet>
        <title>Edit Certificate Template - Field Mapping | NALC</title>
      </Helmet>
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Edit Certificate Template</h1>
        <p className="text-muted-foreground mt-2">
          Step 3: Map data fields to your PDF template
        </p>
      </div>
      
      <div className="flex justify-center mb-6">
        <div className="flex items-center">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              ✓
            </div>
            <span className="text-sm mt-1">Basic Info</span>
          </div>
          <div className="w-12 h-1 bg-primary mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              ✓
            </div>
            <span className="text-sm mt-1">PDF Upload</span>
          </div>
          <div className="w-12 h-1 bg-primary mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              3
            </div>
            <span className="text-sm mt-1 text-primary font-medium">Field Mapping</span>
          </div>
          <div className="w-12 h-1 bg-muted mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              4
            </div>
            <span className="text-sm mt-1">Preview</span>
          </div>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Field Mapping</CardTitle>
          <CardDescription>
            Define areas on the PDF template where dynamic content should be placed.
            Click and drag on the PDF to create fields, then assign data fields to them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PDFTemplateMapper 
            pdfUrl={template.metadata.pdfUrl}
            existingFields={mappedFields}
            onSaveMapping={handleSaveMapping}
          />
          
          <div className="flex justify-between mt-8 pt-4 border-t">
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="button" onClick={handlePrevious}>
                <ChevronLeft className="mr-2 h-4 w-4" /> Previous
              </Button>
            </div>
            <Button type="button" onClick={handleNext}>
              Continue <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditTemplateMappingPage;
